import React, { useContext, useEffect, useState } from "react"
import { UserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { Trash, User, LogOut, Phone, Plus, Check, HelpCircle, InfoCircle, Discord } from "iconoir-react"
import { Button, Modal, ModalBody, ModalHeader } from "flowbite-react"
import { Link } from "react-router-dom"
import { CallbackPhoneNumber } from "@/lib/vibrato-api"
import { formatAnyPhoneNumber } from "@/lib/phone"
import VerifyPhoneNumberModal from "@/components/dashboard/VerifyPhoneNumberModal"

const DELETION_REASONS = [
  "I'm no longer using the service",
  "The service doesn't meet my needs",
  "I found a better alternative",
  "Too expensive for my budget",
  "Privacy concerns",
  "Technical issues or bugs",
  "Difficult to use or understand",
  "Not enough features I need",
  "Poor customer support experience",
  "Other (please specify in comments)"
]

interface AccountDeletionModalProps {
  show: boolean
  onClose: () => void
  selectedReason: string
  setSelectedReason: (reason: string) => void
  comments: string
  setComments: (comments: string) => void
  isSubmitting: boolean
  onSubmit: () => void
}

const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({
  show,
  onClose,
  selectedReason,
  setSelectedReason,
  comments,
  setComments,
  isSubmitting,
  onSubmit
}) => {
  return (
    <Modal show={show} onClose={onClose} size="lg" className="pt-safe">
      <ModalHeader className="border-b border-gray-200/80 px-6 py-4">
        <span className="text-red-600">Request Account Deletion</span>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          <div>
            <p className="text-gray-900 font-medium mb-2">
              We're sorry to see you go. Help us improve by telling us why you're leaving.
            </p>
            <p className="text-sm text-gray-600">
              Your account deletion will be processed within 5-10 business days and we'll be in touch via email to
              confirm.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reason for deletion <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {DELETION_REASONS.map((reason, index) => (
                <label key={index} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="deletion_reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional comments (optional)</label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Please share any additional feedback that could help us improve..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
            />
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-xs text-red-800">
              <strong>Final Warning:</strong> This action cannot be undone. All your data will be permanently deleted
              including call history, contacts, task templates, and account information.
            </p>
          </div>

          <div className="flex justify-center gap-3 pt-4">
            <Button color="gray" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <button
              onClick={onSubmit}
              disabled={!selectedReason.trim() || isSubmitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isSubmitting ? "Submitting..." : "Delete My Account"}
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

const ProfileSettings: React.FC = () => {
  const user = useContext(UserContext)
  const vibratoClient = useContext(VibratoClientContext)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedReason, setSelectedReason] = useState("")
  const [comments, setComments] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [callbackPhoneNumbers, setCallbackPhoneNumbers] = useState<CallbackPhoneNumber[]>([])
  const [showCallbackModal, setShowCallbackModal] = useState(false)

  useEffect(() => {
    // Check if user has already submitted a deletion request
    const submitted = localStorage.getItem(`account_deletion_submitted_${user.id}`)
    if (submitted === "true") {
      setHasSubmitted(true)
    }
  }, [user.id])

  useEffect(() => {
    const fetchCallbackPhoneNumbers = async () => {
      if (vibratoClient) {
        try {
          const callback_phone_numbers = await vibratoClient.getCallbackPhoneNumbers()
          setCallbackPhoneNumbers(callback_phone_numbers)
        } catch (error) {
          console.error("Failed to fetch callback phone numbers:", error)
        }
      }
    }

    fetchCallbackPhoneNumbers()
  }, [vibratoClient])

  const handleDeleteRequest = async () => {
    if (!selectedReason.trim()) {
      return
    }

    try {
      setIsSubmitting(true)
      await vibratoClient.requestAccountDeletion(selectedReason, comments)

      // Mark as submitted in localStorage
      localStorage.setItem(`account_deletion_submitted_${user.id}`, "true")
      setHasSubmitted(true)
      setShowDeleteModal(false)

      // Reset form
      setSelectedReason("")
      setComments("")
    } catch (error) {
      console.error("Failed to submit deletion request:", error)
      // Could add error handling/toast here
    } finally {
      setIsSubmitting(false)
    }
  }

  const removeCallbackNumber = async (uuid: string) => {
    if (vibratoClient) {
      try {
        await vibratoClient.deleteCallbackPhoneNumber(uuid)
        setCallbackPhoneNumbers(callbackPhoneNumbers.filter((number) => number.uuid !== uuid))
      } catch (error) {
        console.error("Failed to remove callback number:", error)
        // Could add error handling/toast here if needed
      }
    }
  }

  return (
    <div className="h-full w-full flex flex-col gap-6">
      {/* Callback Number Modal */}
      {showCallbackModal && (
        <VerifyPhoneNumberModal
          show={showCallbackModal}
          setShow={setShowCallbackModal}
          mode="callback"
          onClose={() => {
            setShowCallbackModal(false)
          }}
          onPhoneNumberVerified={async () => {
            setShowCallbackModal(false)
            // Refresh the callback numbers list
            const callback_phone_numbers = await vibratoClient.getCallbackPhoneNumbers()
            setCallbackPhoneNumbers(callback_phone_numbers)
          }}
        />
      )}

      {/* User Information Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
          </div>

          <div className="space-y-6">
            {/* Email Section */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-900">{user.email}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Your email address cannot be changed</p>
            </div>

            {/* Callback Numbers Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Callback Numbers</label>
                  <p className="text-xs text-gray-500">
                    Numbers where Vibrato can reach you when calls connect or need your input
                  </p>
                </div>
                <button
                  onClick={() => setShowCallbackModal(true)}
                  className="ml-4 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {callbackPhoneNumbers.length > 0 ? (
                <div className="space-y-2">
                  {callbackPhoneNumbers.map((callbackNumber) => (
                    <div
                      key={callbackNumber.uuid}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatAnyPhoneNumber(callbackNumber.country_code, callbackNumber.phone_number)}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {callbackNumber.verified_at ? (
                              <>
                                <Check className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-green-600">Verified</span>
                              </>
                            ) : (
                              <>
                                <HelpCircle className="w-3 h-3 text-amber-500" />
                                <span className="text-xs text-amber-600">Needs verification</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeCallbackNumber(callbackNumber.uuid)}
                        className="p-2 text-red-600 hover:text-red-900 bg-red-50 rounded-lg transition-colors text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">Add a callback number to stay connected</p>
                      <p className="text-xs text-blue-700 leading-relaxed">
                        When Vibrato needs to reach you during a call—like when we're on hold, need clarification, or
                        when a call connects—we'll call your callback number. This ensures you never miss important
                        moments and can jump into conversations seamlessly.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <InfoCircle className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">About</h2>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">Learn more about Vibrato and review our legal documents.</p>

              <div className="space-y-3">
                <a
                  href="https://www.getvibrato.com/c/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Privacy Policy</span>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>

                <a
                  href="https://www.getvibrato.com/c/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Terms of Service</span>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>

                <a
                  href="mailto:contact@getvibrato.com"
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Email Us</span>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </a>

                <a
                  href="https://discord.gg/3Cpz55YqmD"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <Discord className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Join our Discord</span>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>

                <Link
                  to="/review"
                  className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">Leave a Review</span>
                  </div>
                  <svg
                    className="w-4 h-4 text-purple-500 group-hover:text-purple-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Deletion Section */}
      <div className="bg-white rounded-xl border border-red-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Trash className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Delete Account</h2>
          </div>

          <div className="space-y-4">
            {!hasSubmitted && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Account deletion is permanent and cannot be undone. All your data, including
                  call history, contacts, and templates will be permanently removed.
                </p>
              </div>
            )}

            {hasSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Request Submitted:</strong> Your account deletion request has been submitted successfully.
                  We'll process your request within 5-10 business days and will be in touch via email.
                </p>
              </div>
            ) : (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Request Account Deletion
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Log Out Button */}
      <div className="flex justify-center pt-4 pb-4">
        <Link to="/logout">
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium">
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </Link>
      </div>

      {/* Account Deletion Modal */}
      <AccountDeletionModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
        comments={comments}
        setComments={setComments}
        isSubmitting={isSubmitting}
        onSubmit={handleDeleteRequest}
      />
    </div>
  )
}

export default ProfileSettings
